import SpyderAxios from './spyderAxiosConfig'
import firebase from '../config/FirebaseConfig'

// ========== Spyder API Requests ==========

const getBrandSubscriptions = async () => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.get(
    '/subscriptions',
    {
      params: {
        limit: 1000
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  const results = data.data
  return { subscribed: results.subscribed, pending: results.pending }
}

const subscribeToBrand = async (brandId, folderId = null) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.post(
    '/subscriptions',
    {
      brand_ids: [brandId],
      folder_targets: [{ brand_id: brandId, folder_id: folderId }]
    },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

// brandTargets [] { brandId: string, folderId: string | null }
const bulkSubscribeToBrands = async (brandTargets = []) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.post(
    '/subscriptions',
    {
      brand_ids: brandTargets.map(target => target.brandId),
      folder_targets: brandTargets.map(target => ({ brand_id: target.brandId, folder_id: target.folderId }))
    },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

// Advertiser ids are Meta ad library page ids. Ids that aren't linked to a brand yet come back
// under `pending` while the backend builds the brand.
// With a folderId, the server files each brand into that folder (by advertiser id while it's pending).
const subscribeToAdvertisers = async (advertiserIds = [], folderId = null, network = 'facebook') => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.post(
    '/subscriptions',
    {
      advertiser_ids: advertiserIds,
      network,
      ...(folderId ? { folder_targets: advertiserIds.map(advertiserId => ({ advertiser_id: advertiserId, folder_id: folderId })) } : {})
    },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

// Domains must be lowercase bare hostnames (e.g. nike.com). Unknown domains come back under `pending`.
// With a folderId, the server files each brand into that folder (by domain while it's pending).
const subscribeToDomains = async (domains = [], folderId = null) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.post(
    '/subscriptions',
    {
      domains,
      ...(folderId ? { folder_targets: domains.map(domain => ({ domain, folder_id: folderId })) } : {})
    },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

// Unsubscribes from built brands. The server also takes them out of whichever folder they're in
const unsubscribeFromBrands = async (brandIds = []) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.delete(
    '/subscriptions',
    {
      data: {
        brand_ids: brandIds
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data
}

// Cancels pending brands (still being built), by the advertiser ids / domains they were added with. The server
// also takes them out of their folders. Returns { data: { canceled: [{ ref_type, ref }] } }: a brand that finished
// building in the meantime isn't cancelled, so unsubscribe from it by brand id instead.
const cancelPendingBrands = async ({ advertiserIds = [], domains = [] } = {}) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.delete(
    '/subscriptions/pending',
    {
      data: {
        ...(advertiserIds.length ? { advertiser_ids: advertiserIds } : {}),
        ...(domains.length ? { domains } : {})
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data
}

// brandRef: { brand_id } | { advertiser_id } | { domain } (see getBrandFolderRef). A null folderId moves the
// brand to the Default Folder. The server takes it out of any other folder, so it's only ever in one.
const moveBrandToFolder = async (brandRef, folderId = null) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.put(
    '/subscriptions/folder',
    { ...brandRef, folder_id: folderId },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

export default {
  getBrandSubscriptions,
  subscribeToBrand,
  bulkSubscribeToBrands,
  subscribeToAdvertisers,
  subscribeToDomains,
  unsubscribeFromBrands,
  cancelPendingBrands,
  moveBrandToFolder
}
